import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowClockwise, MagnifyingGlass, Package, Warning } from 'shared/icons';
import PageHeader from '../components/layout/PageHeader';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import StandardTable from '../components/ui/StandardTable';
import Input from '../components/ui/Input';
import { Id, Mono } from '../components/ui/Mono';
import LotePainelImpressao from '../components/coletas/LotePainelImpressao';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import { FormatInt } from '../utils/format';
import CarrinhoGeracao from '../components/coletas/CarrinhoGeracao';
import CockpitDashboard from '../components/coletas/CockpitDashboard';
import EnviosAccordion from '../components/coletas/EnviosAccordion';
import { SUBSTATUS_ROTULO } from '../constants/coletas';

// ─────────────────────────────────────────────────────────────────────────────
// COLETAS — a camada a montante do WMS.
//
// O Coletor (OS) e a Bancada já existiam; o que faltava era a fila que os
// alimenta. Sem saber QUAIS envios estão prontos, não há o que virar OS, e sem
// OS não existe picking nem packing.
//
// As abas seguem a ordem do trabalho real, não a ordem do banco: primeiro o
// que precisa de correção HOJE (Demandas), depois o que está pronto para sair
// (Fila), depois a execução (Lotes), e por fim a investigação (Diagnóstico).
// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'envios', label: 'Envios por dia' },
    { id: 'gerar', label: 'Gerar Lote & OS' },
    { id: 'demandas', label: 'Demandas' },
    { id: 'fila', label: 'Fila de envios' },
    { id: 'lotes', label: 'Lotes' },
    { id: 'diagnostico', label: 'Diagnóstico' },
];

const CartaoNumero = ({ rotulo, valor, tom = 'neutro', ajuda }) => (
    <div className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
        <p className="text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">{rotulo}</p>
        <p
            className={`mt-1 font-mono text-2xl font-semibold tabular-nums ${
                tom === 'alerta'
                    ? 'text-warning-text'
                    : tom === 'ok'
                      ? 'text-success-text'
                      : 'text-black dark:text-white'
            }`}
        >
            {FormatInt(valor ?? 0)}
        </p>
        {ajuda ? <p className="mt-1 text-[11px] leading-tight text-mono-500 dark:text-mono-400">{ajuda}</p> : null}
    </div>
);

const ColetasPage = () => {
    const toast = useToast();
    const { checkPermission } = useAuth();
    const podeOperar = checkPermission('wms:operate');

    const [aba, setAba] = useState('dashboard');
    const [resumo, setResumo] = useState(null);
    const [carregandoResumo, setCarregandoResumo] = useState(true);

    const [demandas, setDemandas] = useState(null);
    const [soAntesDaNf, setSoAntesDaNf] = useState(false);
    const [fila, setFila] = useState(null);
    const [lotes, setLotes] = useState(null);
    // O lote aberto no painel de impressão (null = nenhum).
    const [loteImprimindo, setLoteImprimindo] = useState(null);
    const [carregando, setCarregando] = useState(false);

    const [buscaDiag, setBuscaDiag] = useState('');
    const [diag, setDiag] = useState(null);
    const [diagCarregando, setDiagCarregando] = useState(false);

    const carregarResumo = useCallback(async () => {
        setCarregandoResumo(true);
        try {
            setResumo(await api.getColetasResumo());
        } catch (error) {
            toast.error(error?.message || 'Não foi possível carregar o resumo.');
        } finally {
            setCarregandoResumo(false);
        }
    }, [toast]);

    const carregarAba = useCallback(async () => {
        setCarregando(true);
        try {
            if (aba === 'demandas') {
                setDemandas(await api.getColetasDemandas({ incluir_pos_nf: soAntesDaNf ? 'false' : 'true' }));
            } else if (aba === 'fila') {
                setFila(await api.getColetasElegiveis({ limit: 500 }));
            } else if (aba === 'lotes') {
                setLotes(await api.getColetasLotes());
            }
        } catch (error) {
            toast.error(error?.message || 'Não foi possível carregar os dados.');
        } finally {
            setCarregando(false);
        }
    }, [aba, soAntesDaNf, toast]);

    useEffect(() => {
        carregarResumo();
    }, [carregarResumo]);
    useEffect(() => {
        carregarAba();
    }, [carregarAba]);

    // "Atualizar" reconsulta a nossa base, que o worker mantém sincronizada com
    // o ML — é o botão que o analista aperta depois de dar os splits no painel
    // para conferir que nada ficou para trás.
    const atualizarTudo = useCallback(async () => {
        await Promise.all([carregarResumo(), carregarAba()]);
        toast.success('Dados atualizados.');
    }, [carregarResumo, carregarAba, toast]);

    const diagnosticar = useCallback(
        async (codigo) => {
            const alvo = String(codigo || '').trim();
            if (!alvo) return;
            setDiagCarregando(true);
            setDiag(null);
            try {
                setDiag(await api.getColetasDiagnostico(alvo));
            } catch (error) {
                toast.error(error?.message || 'Envio não encontrado.');
            } finally {
                setDiagCarregando(false);
            }
        },
        [toast],
    );

    const colunasDemandas = useMemo(
        () => [
            {
                key: 'external_order_id',
                label: 'Pedido',
                render: (row) => <Id value={row.external_order_id} className="text-xs" />,
            },
            {
                key: 'fase',
                label: 'Fase',
                render: (row) =>
                    row.fase === 'ANTES_DA_NF' ? (
                        <Badge variant="success">Antes da NF — dá pra dividir</Badge>
                    ) : (
                        <Badge variant="warning">NF já emitida</Badge>
                    ),
            },
            { key: 'motivo', label: 'Motivo', getValue: (row) => row.motivo },
            {
                key: 'itens',
                label: 'Itens',
                render: (row) => <span className="text-xs text-mono-600 dark:text-mono-400">{row.itens}</span>,
            },
            {
                key: 'shipping_substatus',
                label: 'Situação no ML',
                getValue: (row) => SUBSTATUS_ROTULO[row.shipping_substatus] || row.shipping_substatus || '—',
            },
        ],
        [],
    );

    const colunasFila = useMemo(
        () => [
            {
                key: 'external_order_id',
                label: 'Pedido',
                render: (row) => <Id value={row.external_order_id} className="text-xs" />,
            },
            { key: 'shipment_id', label: 'Envio', render: (row) => <Mono>{row.shipment_id}</Mono> },
            { key: 'unidades', label: 'Un.', align: 'right', getValue: (row) => FormatInt(row.unidades) },
            { key: 'sku', label: 'SKU', render: (row) => <Mono className="text-xs">{row.sku || '—'}</Mono> },
            {
                key: 'titulo',
                label: 'Produto',
                render: (row) => (
                    <span className="block max-w-[22rem] truncate" title={row.titulo || ''}>
                        {row.titulo || '—'}
                    </span>
                ),
            },
            {
                key: 'shipping_substatus',
                label: 'Situação',
                getValue: (row) => SUBSTATUS_ROTULO[row.shipping_substatus] || row.shipping_substatus || '—',
            },
            {
                key: 'acoes',
                label: '',
                render: (row) => (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            setAba('diagnostico');
                            setBuscaDiag(row.shipment_id);
                            diagnosticar(row.shipment_id);
                        }}
                    >
                        Diagnosticar
                    </Button>
                ),
            },
        ],
        [diagnosticar],
    );

    const colunasLotes = useMemo(
        () => [
            { key: 'codigo', label: 'Lote', render: (row) => <Id value={row.codigo} className="text-xs" /> },
            { key: 'rotulo', label: 'Rótulo' },
            {
                key: 'status',
                label: 'Status',
                render: (row) =>
                    row.status === 'FECHADO' ? (
                        <Badge variant="neutral">FINALIZADO</Badge>
                    ) : row.impressao_iniciada_em ? (
                        // Mandado à impressora e ainda sem "deu certo": é o que
                        // "Recuperar" retoma.
                        <Badge variant="warning">IMPRIMINDO</Badge>
                    ) : (
                        <Badge variant="success">{row.status}</Badge>
                    ),
            },
            { key: 'total', label: 'Total', align: 'right', getValue: (row) => FormatInt(row.total) },
            { key: 'impressos', label: 'Impressos', align: 'right', getValue: (row) => FormatInt(row.impressos) },
            { key: 'pendentes', label: 'Pendentes', align: 'right', getValue: (row) => FormatInt(row.pendentes) },
            { key: 'dono_nome', label: 'Aberto por', getValue: (row) => row.dono_nome || '—' },
            {
                key: 'acoes',
                label: '',
                render: (row) =>
                    podeOperar ? (
                        <div className="flex justify-end gap-1">
                            {/* IMPRIMIR abre a impressão em etapas; o lote só
                                encerra pelo "deu certo — finalizar" de lá (o
                                "Fechar" solto aqui devolvia os envios para a
                                fila). Impressão começada e não finalizada vira
                                RECUPERAR. */}
                            {row.status !== 'FECHADO' ? (
                                row.impressao_iniciada_em ? (
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => setLoteImprimindo({ ...row, modo: 'recuperar' })}
                                    >
                                        Recuperar
                                    </Button>
                                ) : (
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setLoteImprimindo({ ...row, modo: 'imprimir' })}
                                    >
                                        Imprimir
                                    </Button>
                                )
                            ) : (
                                // Finalizado: reabrir devolve o lote para reimprimir
                                // (os envios continuam impressos).
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={async () => {
                                        await api.reabrirColetaLote(row.id);
                                        toast.success(`Lote ${row.codigo} reaberto.`);
                                        await Promise.all([carregarResumo(), carregarAba()]);
                                    }}
                                >
                                    Reabrir
                                </Button>
                            )}
                        </div>
                    ) : null,
            },
        ],
        [podeOperar, toast, carregarResumo, carregarAba],
    );

    return (
        <div className="space-y-6">
            <PageHeader
                title="Coletas"
                subtitle="Gestão dos envios do Mercado Livre: o que precisa de correção, o que está pronto para sair e por onde cada etiqueta passou."
                actions={
                    <div className="flex flex-wrap items-center gap-2">
                        <Button variant="secondary" onClick={atualizarTudo}>
                            <ArrowClockwise size={16} className="mr-1.5" />
                            Atualizar
                        </Button>
                    </div>
                }
            />

            {carregandoResumo ? (
                <Skeleton className="h-24 w-full" />
            ) : (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                    <CartaoNumero rotulo="Prontos p/ imprimir" valor={resumo?.imprimiveis} tom="ok" />
                    <CartaoNumero rotulo="Aguardando NF" valor={resumo?.aguardando_nf} tom="alerta" />
                    <CartaoNumero
                        rotulo="Baixados, não impressos"
                        valor={resumo?.baixados_nao_impressos}
                        ajuda="Etiqueta em mãos, ainda não saiu"
                    />
                    <CartaoNumero rotulo="Impressos" valor={resumo?.impressos} />
                    <CartaoNumero rotulo="Lotes abertos" valor={resumo?.lotes_abertos} />
                    <CartaoNumero rotulo="Reservados em OS" valor={resumo?.reservados_em_os} />
                </div>
            )}

            <div className="border-b border-mono-200 dark:border-mono-800">
                <nav className="-mb-px flex gap-1 overflow-x-auto">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setAba(t.id)}
                            className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                                aba === t.id
                                    ? 'border-black text-black dark:border-white dark:text-white'
                                    : 'border-transparent text-mono-500 hover:text-black dark:text-mono-400 dark:hover:text-white'
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </nav>
            </div>

            {aba === 'dashboard' ? <CockpitDashboard /> : null}

            {aba === 'envios' ? <EnviosAccordion /> : null}

            {aba === 'gerar' ? <CarrinhoGeracao podeOperar={podeOperar} onGerou={carregarResumo} /> : null}

            {aba === 'demandas' ? (
                <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-mono-200 bg-mono-50 p-4 dark:border-mono-800 dark:bg-mono-900">
                        <p className="max-w-[68ch] text-sm text-mono-600 dark:text-mono-400">
                            <Warning size={15} className="mr-1 inline text-warning-text" />
                            Pedidos com <strong>mais de um SKU</strong> ou <strong>mais de uma unidade</strong>, que
                            precisam ser divididos. Diferente do checkout, aqui eles aparecem{' '}
                            <strong>antes da NF ser emitida</strong> — que é a janela em que o split ainda é uma
                            edição no painel, e não um cancelamento.
                        </p>
                        <label className="flex shrink-0 items-center gap-2 text-sm text-mono-700 dark:text-mono-300">
                            <input
                                type="checkbox"
                                checked={soAntesDaNf}
                                onChange={(e) => setSoAntesDaNf(e.target.checked)}
                                className="h-4 w-4"
                            />
                            Só os que ainda dá para corrigir
                        </label>
                    </div>
                    {demandas?.resumo ? (
                        <div className="flex flex-wrap gap-2">
                            <Badge variant="neutral">{FormatInt(demandas.resumo.total)} no total</Badge>
                            <Badge variant="success">
                                {FormatInt(demandas.resumo.antes_da_nf)} antes da NF
                            </Badge>
                            <Badge variant="warning">{FormatInt(demandas.resumo.nf_emitida)} já com NF</Badge>
                        </div>
                    ) : null}
                    {carregando ? (
                        <Skeleton className="h-64 w-full" />
                    ) : (
                        <StandardTable
                            columns={colunasDemandas}
                            rows={demandas?.results ?? []}
                            rowKey={(row, i) => row.sale_id || i}
                            countLabel="pedidos"
                            exportFileName="coletas-demandas"
                            emptyMessage="Nenhum pedido fora da regra — todos são 1 SKU × 1 unidade."
                        />
                    )}
                </div>
            ) : null}

            {aba === 'fila' ? (
                carregando ? (
                    <Skeleton className="h-64 w-full" />
                ) : (
                    <StandardTable
                        columns={colunasFila}
                        rows={fila?.results ?? []}
                        rowKey={(row, i) => row.shipment_id || i}
                        countLabel="envios"
                        exportFileName="coletas-fila"
                        emptyMessage="Nenhum envio elegível: ou não há NF emitida, ou todos já estão em lote/OS."
                    />
                )
            ) : null}

            {aba === 'lotes' ? (
                carregando ? (
                    <Skeleton className="h-64 w-full" />
                ) : (
                    <StandardTable
                        columns={colunasLotes}
                        rows={lotes?.results ?? []}
                        rowKey={(row, i) => row.id || i}
                        countLabel="lotes"
                        exportFileName="coletas-lotes"
                        emptyMessage="Nenhum lote criado ainda."
                    />
                )
            ) : null}

            <LotePainelImpressao
                lote={loteImprimindo}
                onFechar={() => setLoteImprimindo(null)}
                onMudou={() => Promise.all([carregarResumo(), carregarAba()])}
            />

            {aba === 'diagnostico' ? (
                <div className="space-y-4">
                    <div className="flex flex-wrap items-end gap-2">
                        <div className="min-w-[280px] flex-1">
                            <Input
                                label="Envio, pedido ou etiqueta"
                                value={buscaDiag}
                                onChange={(e) => setBuscaDiag(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && diagnosticar(buscaDiag)}
                                placeholder="Cole o shipment_id"
                            />
                        </div>
                        <Button variant="primary" onClick={() => diagnosticar(buscaDiag)}>
                            <MagnifyingGlass size={16} className="mr-1.5" />
                            Consultar
                        </Button>
                    </div>

                    {diagCarregando ? <Skeleton className="h-48 w-full" /> : null}

                    {diag ? (
                        <div className="space-y-4">
                            <div className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                                <p className="text-xs uppercase tracking-wide text-mono-500">Situação</p>
                                <p className="mt-1 text-lg font-semibold text-black dark:text-white">
                                    {diag.veredito}
                                </p>
                                {diag.venda ? (
                                    <div className="mt-3 flex flex-wrap gap-2 text-xs text-mono-600 dark:text-mono-400">
                                        <Badge variant="neutral">Pedido {diag.venda.external_order_id}</Badge>
                                        <Badge variant="neutral">
                                            {SUBSTATUS_ROTULO[diag.venda.shipping_substatus] ||
                                                diag.venda.shipping_substatus ||
                                                '—'}
                                        </Badge>
                                        {diag.venda.shipping_logistic_type ? (
                                            <Badge variant="neutral">{diag.venda.shipping_logistic_type}</Badge>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>

                            <div className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-black dark:text-white">
                                    <Package size={16} className="text-mono-500" />
                                    Linha do tempo
                                </h3>
                                {diag.eventos?.length ? (
                                    <ul className="space-y-2">
                                        {diag.eventos.map((ev, i) => (
                                            <li
                                                key={`${ev.em}-${i}`}
                                                className="flex flex-wrap items-baseline gap-2 border-b border-mono-100 pb-2 text-sm last:border-0 dark:border-mono-900"
                                            >
                                                <Mono className="text-xs text-mono-500">
                                                    {String(ev.em).slice(0, 19).replace('T', ' ')}
                                                </Mono>
                                                <Badge variant="neutral">{ev.fase}</Badge>
                                                <span className="text-mono-700 dark:text-mono-300">{ev.texto}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-sm text-mono-500">
                                        Nenhum evento registrado para este envio ainda.
                                    </p>
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : null}

        </div>
    );
};

export default ColetasPage;
